import cx from 'classnames'
import React from 'react'
import { Layout } from '../../Layout'
import './styles.scss'

export const maxWidthClass = 'app-max-width-layout'

enum ContentSpacing {
  // !Warning: Please sync those values with the scss
  None = 'none',
  Small = 'small', // 10px -- $spacing-small-2
  Middle = 'middle', // 20px -- $spacing-medium-2
  Default = 'default', // 40px -- $spacing-large-3
  Large = 'large' // 60px -- $spacing-large-4
}
type ContentSpacingType = `${ContentSpacing}`

enum Justify {
  Start = 'start',
  Center = 'center'
}
export type MaxWidthLayoutJustifyType = `${Justify}`

/**
 * This is the possible values of maxWidth. Those are base on our breakpoints
 * but also on our different designs use cases which is why we have values like
 * 'hd' that limits the width to 1080px
 */
export enum ContentWidthDeviceSize {
  // !Warning: Please sync those values with the scss
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Don't really apply to numbers
  '488px' = 'xs',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Don't really apply to numbers
  '576px' = 'sm',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Don't really apply to numbers
  '768px' = 'md',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Don't really apply to numbers
  '1024px' = 'lg',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Don't really apply to numbers
  '1280px' = 'xl',
  '1600px' = 'xxl',

  // Values not linked to any specific breaking point
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Don't really apply to numbers
  '1080px' = 'hd',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Don't really apply to numbers
  '680px' = 'sm-2'
}
export type ContentWidthDeviceSizeType = `${ContentWidthDeviceSize}`

interface MaxWidthLayoutProps {
  id?: string
  // Customize the container using your own classes
  className?: string
  // Add vertical spacing using our options. Control top and bottom spacing separately by passing an object
  spacing?: ContentSpacingType | { top?: ContentSpacingType; bottom?: ContentSpacingType }
  // Define the width of your internal layout content.  The word internal is important here
  // as you don't want to use this layout too soon in the DOM tree to avoid issues where
  // children components would want to display headers or background on the full screen width
  maxWidth?: ContentWidthDeviceSizeType
  /**
   * Align the content to start or center of page
   * @defaultValue center
   */
  justify?: 'start' | 'center'
  children: React.ReactNode
  footer?: React.ReactNode
  bgColor?: 'neutral-300' | 'neutral-200'
  style?: React.CSSProperties
}

const MaxWidthLayoutContext = React.createContext<Omit<MaxWidthLayoutProps, 'children' | 'className'> | null>(null)
const useMaxWidthLayout = () => {
  const context = React.useContext(MaxWidthLayoutContext)
  return context || { spacing: undefined, maxWidth: undefined, bgColor: undefined, justify: undefined }
}

const MaxWidthLayoutContent = React.forwardRef<HTMLDivElement, MaxWidthLayoutProps>(
  (
    {
      children,
      spacing = ContentSpacing.Default,
      maxWidth = ContentWidthDeviceSize['1024px'],
      className,
      bgColor,
      justify,
      style,
      id,
      ...rest
    },
    ref
  ) => {
    const {
      spacing: inheritedSpacing,
      maxWidth: inheritedMaxWidth,
      bgColor: inheritedBgColor,
      justify: inheritedJustify
    } = useMaxWidthLayout()
    const applyBgColor = inheritedBgColor || bgColor
    const Container = applyBgColor ? 'div' : React.Fragment
    const justifyValue = inheritedJustify ?? justify
    const unwrappedTopSpacing =
      (typeof inheritedSpacing === 'object' ? inheritedSpacing.top : inheritedSpacing) ||
      (typeof spacing === 'object' ? spacing.top : spacing) ||
      ContentSpacing.Default

    const unwrappedBottomSpacing =
      (typeof inheritedSpacing === 'object' ? inheritedSpacing.bottom : inheritedSpacing) ||
      (typeof spacing === 'object' ? spacing.bottom : spacing) ||
      ContentSpacing.Default

    return (
      <Container ref={ref} {...(applyBgColor && { className: cx(`u-bg-color--${bgColor}`, maxWidthClass) })}>
        <section
          id={id}
          data-sectionid="max-width-layout-content"
          data-testid="max-width-layout-content"
          className={cx(
            `${maxWidthClass}--${inheritedMaxWidth || maxWidth}`,
            `${maxWidthClass}-spacing-top--${unwrappedTopSpacing}`,
            `${maxWidthClass}-spacing-bottom--${unwrappedBottomSpacing}`,
            justifyValue === 'start' && `${maxWidthClass}--justify-start`,
            className
          )}
          // isXSmall should not be used at this level
          style={style}
          {...rest}
        >
          {children}
        </section>
      </Container>
    )
  }
)
MaxWidthLayoutContent.displayName = 'MaxWidthLayoutContent'

export function MaxWidthLayout({
  children,
  spacing,
  maxWidth,
  className,
  bgColor,
  footer,
  justify,
  ...rest
}: MaxWidthLayoutProps) {
  const value = React.useMemo(() => ({ maxWidth, spacing, bgColor, justify }), [maxWidth, spacing, bgColor, justify])
  return (
    <MaxWidthLayoutContext.Provider value={value}>
      <Layout
        data-testid="max-width-layout"
        id="max-width-layout"
        className={cx(bgColor && `u-bg-color--${bgColor}`, className)}
        {...rest}
      >
        {children}
        {footer ? <Layout.Footer className={`${maxWidthClass}-footer`}>{footer}</Layout.Footer> : null}
      </Layout>
    </MaxWidthLayoutContext.Provider>
  )
}

MaxWidthLayout.Section = MaxWidthLayoutContent
export default MaxWidthLayout
